import { useCallback } from 'react';

import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';

import { formatString, isDefined, isNotDefined } from '@hsp/utilities';

import { SecretKeyRequestProps } from '@c/notifications/components/secret-key-request/types/secret-key-request.props';
import { useDeleteNotification } from '@c/notifications/hooks';
import { INotification, ISecretKeyApprove, NotificationTarget } from '@c/notifications/types';
import { notificationQueryKeys } from '@c/notifications/utilities';

import { useActions } from '@h/actions';
import { useAppSelector } from '@h/index';

import { useExternalListMutation } from '@p/ab/shared/hooks/queries';

import { selectUser } from '@s/selectors';

import { IResult } from '@t/api/common';

import { s } from '@u/strings';

import { signalRService } from 'services/signalr.service';

export const useSecretKeyRequestConfiguration = ({
  id,
  name,
  notificationId,
  notify,
}: Pick<SecretKeyRequestProps, 'id' | 'name' | 'notificationId' | 'notify'>) => {
  const user = useAppSelector(selectUser);

  const { dispatch } = useActions();
  const { deleteNotification } = useDeleteNotification();

  const { mutateAsync: getExternalList } = useExternalListMutation();
  const queryClient = useQueryClient();

  const queryKey = notificationQueryKeys.all;

  const handleRemove = useCallback(() => {
    deleteNotification(notificationId).then(({ success }) => {
      if (success) {
        dispatch('remove-notification', notificationId);
        toast.dismiss(notificationId);

        queryClient.setQueryData<IResult<INotification[]>>(queryKey, (oldData) => {
          if (isDefined(oldData)) {
            const { dataContainer } = oldData;

            if (isNotDefined(dataContainer)) {
              return oldData;
            }

            oldData = {
              ...oldData,
              dataContainer: [...dataContainer.filter(({ id: itemId }) => itemId !== notificationId)],
            };

            return oldData;
          }

          return oldData;
        });
      }
    });
  }, [deleteNotification, dispatch, notificationId, queryClient, queryKey]);

  const handleApprove = useCallback(() => {
    if (isNotDefined(user) || isNotDefined(id) || isNotDefined(name)) {
      return;
    }

    getExternalList(id).then(({ dataContainer }) => {
      if (isNotDefined(dataContainer)) {
        return;
      }

      const { secretKey } = dataContainer;

      if (isNotDefined(secretKey)) {
        return;
      }

      signalRService.sendMessageToUser<ISecretKeyApprove>(
        {
          isError: false,
          message: formatString(
            s(['Site.Pages.AudienceBuilder.Create.ExternalLists.ApproveSecretKey']),
            user.displayName,
            name
          ),
          payload: {
            secretKey,
          },
        },
        notify,
        NotificationTarget.SecretKeyApprove
      );

      handleRemove();
    });
  }, [getExternalList, handleRemove, id, name, notify, user]);

  const handleDeny = useCallback(() => {
    if (isNotDefined(user) || isNotDefined(name)) {
      return;
    }

    signalRService.sendMessageToUser(
      {
        isError: false,
        message: formatString(
          s(['Site.Pages.AudienceBuilder.Create.ExternalLists.DenySecretKey']),
          user.displayName,
          name
        ),
      },
      notify,
      NotificationTarget.SecretKeyDeny
    );
    handleRemove();
  }, [handleRemove, name, notify, user]);

  return {
    handleApprove,
    handleDeny,
  };
};
